/**
 * ``useAuditEvents`` — TanStack Query hook around
 * ``GET /api/admin/audit/events``.
 *
 * Powers the admin Audit Log panel (storyboard Flow J; Stream G G2).
 * The surface is **admin-only at MVP** (the 2026-06-08 §6 ruling): the
 * BFF gates the endpoint with ``require_roles("admin")`` and this hook
 * mirrors that with the same role gate so a non-admin never issues the
 * request (defence-in-depth; the panel is also filtered out of the
 * add-panel menu per REQ-UI-047). Mirrors
 * :func:`entity-details/use-recent-errors` in shape.
 *
 * The list is filterable + offset-paginated via an opaque cursor; the
 * filter object drives the query key so changing any filter refetches.
 * Reading the audit log is itself NOT audited (Flow J / ADR-006) — only
 * the export is, and that happens server-side on the export GET.
 *
 * Cross-references:
 *   - REQ-UI-022 (Audit Log surface); REQ-LOG-019..022 (audit query).
 *   - REQ-AUTH-022 (BFF-side RBAC; admin-only per the §6 ruling).
 *   - Flow J (docs/engineering/detail/storyboards/flows/J-audit-log).
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { Role } from "@/auth/session-types";
import type { AuditSortBy, AuditSortDir } from "@/audit/audit-display";
import type { components } from "@/lib/api-types.gen";

export type AuditEventItem = components["schemas"]["AuditEventItem"];
export type AuditEventPage = components["schemas"]["AuditEventPage"];

/** Flow J outcome filter values (mapped server-side to OUTCOME_*). */
export type AuditOutcomeFilter = "all" | "success" | "failure" | "denied";

/** Export wire formats the BFF supports (Flow J Frame 4). */
export type AuditExportFormat = "csv" | "jsonl";

/**
 * Roles permitted to read the Audit Log. Admin-only at MVP per the
 * 2026-06-08 §6 ruling (REQ-UI-022); broadening to a role-filtered view
 * is post-MVP (gated on the sensitive-event-type list being populated).
 */
export const AUDIT_LOG_READER_ROLES: readonly Role[] = ["admin"];

/** Matches the BFF default + cap (``_audit_routes`` DEFAULT/MAX_AUDIT_LIMIT). */
export const DEFAULT_AUDIT_LIMIT = 100;

/** Short stale window — the audit log is append-only + low-rate. */
export const AUDIT_LOG_STALE_TIME_MS = 15_000;

/**
 * Filter + pagination state the panel owns and passes to the hook.
 * ``start`` / ``end`` are ISO-8601 strings (omitted ⇒ the BFF default
 * 24h window). ``cursor`` is the opaque ``next_cursor`` from a prior page.
 */
export interface AuditEventsFilter {
  start?: string | null;
  end?: string | null;
  actor?: string | null;
  /** ``action_kind`` (repeatable); empty ⇒ all kinds. */
  actionKind?: readonly string[];
  outcome?: AuditOutcomeFilter;
  q?: string | null;
  /** When false (default) the BFF excludes ``connector.auth.success``. */
  includeConnectorAuth?: boolean;
  /**
   * Server-side sort column (``sort_by``). DB-backed columns only;
   * omitted ⇒ the BFF default (``time``). Sort is applied before
   * pagination so it holds across pages (the panel re-sends it on Next /
   * Previous).
   */
  sortBy?: AuditSortBy;
  /** Server-side sort direction (``sort_dir``); omitted ⇒ BFF default ``desc``. */
  sortDir?: AuditSortDir;
  cursor?: string | null;
  limit?: number;
}

/**
 * Stable React Query key for a filter. ``actionKind`` is sorted so the
 * key is order-insensitive (the same set always hits the same cache row).
 */
export function auditEventsQueryKey(filter: AuditEventsFilter): readonly unknown[] {
  return [
    "audit-events",
    filter.start ?? null,
    filter.end ?? null,
    filter.actor ?? null,
    [...(filter.actionKind ?? [])].sort(),
    filter.outcome ?? "all",
    filter.q ?? null,
    filter.includeConnectorAuth ?? false,
    filter.sortBy ?? null,
    filter.sortDir ?? null,
    filter.cursor ?? null,
    filter.limit ?? DEFAULT_AUDIT_LIMIT,
  ] as const;
}

/** Append the shared filter params (used by both the list query + export). */
function appendFilterParams(params: URLSearchParams, filter: AuditEventsFilter): void {
  if (filter.start) params.set("start", filter.start);
  if (filter.end) params.set("end", filter.end);
  if (filter.actor) params.set("actor", filter.actor);
  for (const kind of filter.actionKind ?? []) params.append("action_kind", kind);
  if (filter.outcome && filter.outcome !== "all") params.set("outcome", filter.outcome);
  if (filter.q) params.set("q", filter.q);
  if (filter.includeConnectorAuth) params.set("include_connector_auth", "true");
}

/**
 * Build the ``/api/admin/audit/events`` query string for a filter + page.
 *
 * Sort (``sort_by`` / ``sort_dir``) is appended HERE — not in
 * :func:`appendFilterParams` — because the export GET shares the filter
 * params but deliberately ignores sort (the locked decision: export
 * honours the active filters, never the table's visible-columns or sort).
 */
export function buildAuditEventsQuery(filter: AuditEventsFilter): string {
  const params = new URLSearchParams();
  appendFilterParams(params, filter);
  if (filter.sortBy) params.set("sort_by", filter.sortBy);
  if (filter.sortDir) params.set("sort_dir", filter.sortDir);
  if (filter.cursor) params.set("cursor", filter.cursor);
  params.set("limit", String(filter.limit ?? DEFAULT_AUDIT_LIMIT));
  return params.toString();
}

/**
 * Build the same-origin ``/api/admin/audit/events/export`` URL for the
 * current filter. The export is a GET file-download (Content-Disposition
 * attachment) — it cannot use :func:`apiFetch` (which parses JSON), so
 * the panel triggers a browser download against this URL. The session
 * cookie rides on the same-origin navigation; GET ⇒ no CSRF header
 * (REQ-UI-035 — same-origin only). The GET emits ``audit.export.created``
 * server-side (Flow J Frame 4).
 */
export function buildAuditExportUrl(
  filter: AuditEventsFilter,
  options: { format: AuditExportFormat; includePayload: boolean },
): string {
  const params = new URLSearchParams();
  appendFilterParams(params, filter);
  params.set("format", options.format);
  if (options.includePayload) params.set("include_payload", "true");
  return `/api/admin/audit/events/export?${params.toString()}`;
}

/**
 * Fetch a page of audit events for the given filter.
 *
 * Disabled (no request) for non-admin sessions. 401/403 are not retried
 * (the BFF's RBAC verdict is authoritative); transient errors retry
 * twice. The response is shape-guarded at the trust boundary
 * (coding-standards §2.2) before it reaches the panel.
 */
export function useAuditEvents(
  filter: AuditEventsFilter,
): UseQueryResult<AuditEventPage, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(AUDIT_LOG_READER_ROLES);
  return useQuery<AuditEventPage, ApiError>({
    queryKey: auditEventsQueryKey(filter),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/admin/audit/events?${buildAuditEventsQuery(filter)}`,
        { signal },
      );
      if (!isAuditEventPage(raw)) {
        console.error("[meridian/audit/use-audit-events] response failed shape validation", raw);
        throw new ApiError(500, raw, "audit-events response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: AUDIT_LOG_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isAuditEventPage(value: unknown): value is AuditEventPage {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return Array.isArray(obj.items) && typeof obj.total === "number";
}
